import React, { useState, ChangeEvent } from 'react';
import styled from 'styled-components';
import { motion, LazyMotion, domAnimation, m } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiEye, FiTrash2, FiLogOut, FiUser, FiPhone, FiHardDrive } from 'react-icons/fi';
import QRScanner from '../components/QRScanner';

import SurfaceProductView from '../components/SurfaceProductView';  
import LogoutModal from '../components/LogoutModal';

const PageContainer = styled(motion.div)`
  min-height: 100vh;
  background: white;
  padding: 20px;
  padding-bottom: 80px; /* Add padding for fixed buttons */
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 30px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #333;
  padding: 8px;
`;

const HeaderTitle = styled.span`
  font-size: 16px;
  color: #333;
  font-weight: 500;
`;

const Logo = styled.div`
  width: 45px;
  height: 45px;
  background-color: #ff0000;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: white;
  font-weight: bold;
  font-size: 10px;
  text-align: center;
  line-height: 1.2;
`;

const Content = styled.div`

  display: flex;
  flex-direction: column;
  gap: 20px;
  
`;

const Section = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(207, 49, 49, 0.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  color: #ff0000;
  margin-bottom: 0;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  letter-spacing: 0.5px;
  padding: 15px;
  background: rgba(255, 0, 0, 0.03);
  border-bottom: 1px solid rgba(207, 49, 49, 0.1);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 25px;
  padding: 20px;
  background: white;
  border-radius: 12px;
  padding-top: 30px;
`;

const InputContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 8px;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  display: flex;
  align-items: center;
  z-index: 1;
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const InputLabel = styled.label<{ hasValue: boolean; isDisabled?: boolean }>`
  position: absolute;
  left: ${props => props.hasValue ? '0px' : '45px'};
  top: ${props => props.hasValue ? '-10px' : '50%'};
  transform: translateY(-50%);
  background: ${props => props.hasValue ? 'white' : 'transparent'};
  padding: ${props => props.hasValue ? '0 4px' : '0'};
  font-size: ${props => props.hasValue ? '12px' : '14px'};
  color: ${props => props.isDisabled ? 'rgb(250, 154, 154)' : props.hasValue ? 'rgb(250, 154, 154)' : '#666'};
  transition: all 0.2s ease;
  pointer-events: none;
  font-weight: ${props => props.hasValue ? '600' : '400'};
  letter-spacing: ${props => props.hasValue ? '0.5px' : '0'};
  text-transform: ${props => props.hasValue ? 'uppercase' : 'none'};
`;

const Input = styled.input<{ isDisabled?: boolean; hasIcon?: boolean }>`
  width: 100%;
  padding: 15px;
  padding-left: ${props => props.hasIcon ? '45px' : '15px'};
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  font-size: 14px;
  outline: none;
  background: ${props => props.isDisabled ? '#f8f8f8' : '#fff'};
  transition: all 0.3s ease;
  color: ${props => props.isDisabled ? '#666' : '#333'};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
  
  &::placeholder {
    color: transparent;
  }
  
  &:not(:disabled):hover {
    border-color: rgba(255, 0, 0, 0.3);
    background: white;
  }
  
  &:focus {
    border-color: #ff0000;
    box-shadow: 0 0 0 3px rgba(255, 0, 0, 0.1);
    background: white;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.8;
    border-color: rgba(47, 34, 34, 0.12);
  }

  &:focus + ${InputLabel} {
    left: 0px;
    top: -10px;
    font-size: 12px;
    color: rgb(250, 154, 154);
    background: white;
    padding: 0 4px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
`;

const ScanButton = styled(motion.button)`
  width: 100%;
  padding: 12px;
  background: #ff0000;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  margin: 5px 0 15px 0;
`;

const ProductList = styled.div`
  margin-top: 10px;
`;

const ProductListTitle = styled.h2`
  font-size: 14px;
  color: #333;
  margin-bottom: 10px;
  font-weight: 700;
  text-align: center;
`;

interface Product {
  code: string;
  room: string;
  application: string;
}

const ProductItem = styled.div`
  background: rgb(248, 247, 247);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid rgba(207, 49, 49, 0.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    background: white;
    border-color: rgba(207, 49, 49, 0.3);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const ProductInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  flex: 1;
  margin-right: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const InfoColumn = styled.div`
  h3 {
    font-size: 11px;
    color: rgb(250, 154, 154);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }
  
  p {
    font-size: 14px;
    color: #333;
    font-weight: 500;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: #ff0000;
  border: none;
  cursor: pointer;
  color: white;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e60000;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  padding: 15px;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  z-index: 10;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
`;

const Button = styled(motion.button)<{ variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 14px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: ${props => props.variant === 'primary' ? '#ff0000' : '#f8f8f8'};
  color: ${props => props.variant === 'primary' ? 'white' : '#333'};
  font-family: 'Poppins', sans-serif;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

const RoomSection = styled.div`
  margin-bottom: 20px;
  background: rgba(255, 0, 0, 0.03);
  border-radius: 8px;
  overflow: hidden;
`;

const RoomHeader = styled.div`
  padding: 10px 15px;
  background: rgba(255, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(207, 49, 49, 0.1);
`;

const RoomTitle = styled.h4`
  font-size: 14px;
  color: #ff0000;
  margin: 0;
  font-weight: 600;
  text-transform: uppercase;
`;

const RoomArea = styled.span`
  font-size: 12px;
  color: #666;
  font-weight: 500;
`;

const RoomProducts = styled.div`
  padding: 10px;
`;

const MainSection = styled(Section)`
  margin-bottom: 20px;
`;

const SubSection = styled.div`
  margin-bottom: 15px;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(207, 49, 49, 0.1);
`;

const SubSectionTitle = styled.h4`
  font-size: 13px;
  color: #ff0000;
  margin: 0;
  font-weight: 600;
  text-transform: uppercase;
  padding: 12px 15px;
  background: rgba(255, 0, 0, 0.03);
  border-bottom: 1px solid rgba(207, 49, 49, 0.1);
`;

const SubSectionContent = styled.div`
  padding: 10px;
`;

const DateInput = styled(Input)`
  background: #f8f8f8;
  color: #333;
  font-weight: 500;
  cursor: default;
  
  &:focus {
    border-color: rgba(0, 0, 0, 0.1);
    box-shadow: none;
  }
`;

const SalesAdvisoryInput = styled(Input)`
  background: #f8f8f8;
  color: #333;
  font-weight: 500;
  cursor: default;
  
  &:focus {
    border-color: rgba(0, 0, 0, 0.1);
    box-shadow: none;
  }
`;

const dummyProducts = [
  { code: '97856', room: 'Mother\'s room', area: '120 sqft' },
  { code: '97857', room: 'Mother\'s room', area: '80 sqft' },
  { code: '97858', room: 'Living room', area: '200 sqft' },
  { code: '97859', room: 'Living room', area: '150 sqft' },
  { code: '97860', room: 'Kitchen', area: '100 sqft' },
];

const dummyProductDetails = {
  code: 'PCS/SUR/TIL/2435',
  images: [
    '/images/colored-realistic-ceramic-floor-tiles-horizontal-set-with-squares-different-types-styles-tiles_1284-29013.jpg',
    '/images/3d-rendering-luxury-modern-living-room-with-leather-sofa-lamp_105762-2255.jpg',
    '/images/traditional-house-architecture_23-2151050966.jpg',
  ],
  details: {
    technical: {
      material: 'Porcelain',
      colorTexture: 'gray',
      variation: 'No',
      repetition: 'Yes',
      recommendation: 'Wall/Floor',
      traffic: 'Medium',
      edgean: 'Normal',
      spacer: '2',
      recommendedAdhesive: '5',
      recommendedGrout: 'Thin',
      antiSlip: 'yes',
    },
    size: {
      length: '520.00',
      width: '170.00',
      tesseraSize: '124.90',
      thickness: '0.00',
      dia: '0.00',
    },
    options: {
      colorOption: 'Blue',
      sizeOption: '70.00',
    },
  },
};

const NewEnquiry = () => {
  const navigate = useNavigate();
  const [showScanner, setShowScanner] = useState(false);
  const [showProductView, setShowProductView] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState<string | null>(null);
  const [clientName, setClientName] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [architectName, setArchitectName] = useState('');
  const [architectNumber, setArchitectNumber] = useState('');
  const [salesAdvisoryName] = useState('Karthik Acharya');
  const [products, setProducts] = useState<Product[]>([
    {
      code: 'PCS/SUR/TIL/1178',
      room: "Mother's room",
      application: 'Wall'
    },
    // ... other initial products if any
  ]);
  const [currentDate] = useState(
    new Date().toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  );

  const handleClientNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setClientName(e.target.value);
  };

  const handleContactNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    setContactNumber(e.target.value);
  };

  const handleArchitectNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setArchitectName(e.target.value);
  };

  const handleArchitectNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    setArchitectNumber(e.target.value);
  };

  const handleScanClick = async () => {
    try {
      // Directly request camera access which will trigger the browser's permission prompt
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment"
        },
        audio: false
      });
      
      // If permission is granted, stop the test stream and show scanner
      stream.getTracks().forEach(track => track.stop());
      setShowScanner(true);
      
    } catch (err) {
      // Permission was denied or there was a technical error
      // We don't show any alerts - user has already made their choice in the browser prompt
      console.error('Camera access error:', err);
    }
  };

  const handleScan = (result: string) => {
    setProducts([...products, { 
      code: result, 
      room: "Mother's room", 
      application: 'Wall'
    }]);
  };

  const handleRemoveProduct = (index: number) => {
    setProducts(products.filter((_, i) => i !== index));
  };

  const handleRemoveRoom = (roomName: string) => {
    setProducts(products.filter(product => product.room !== roomName));
  };

  const handleViewProduct = () => {
    setShowProductView(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/home');
  };

  const handleDeleteClick = (roomName: string) => {
    setRoomToDelete(roomName);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (roomToDelete) {
      handleRemoveRoom(roomToDelete);
      setRoomToDelete(null);
    }
    setShowDeleteConfirm(false);
  };

  return (
    <LazyMotion features={domAnimation}>
      <PageContainer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <Header>
          <HeaderLeft>
            <BackButton onClick={() => navigate('/home')}>
              <FiArrowLeft />
            </BackButton>
            <HeaderTitle>New Enquiry</HeaderTitle>
          </HeaderLeft>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Logo>
              STYLE<br /><span style={{fontWeight: '900'}}>BOX</span>
            </Logo>
            <Button onClick={() => setShowLogoutConfirm(true)}>
              <FiLogOut size={16} />
            </Button>
          </div>
        </Header>

        <Content>
          <Section>
            <SectionTitle>CLIENT DETAILS - {new Date().toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            })}</SectionTitle>
            <Form onSubmit={handleSubmit}>
              <InputContainer>
                <InputIcon>
                  <FiUser />
                </InputIcon>
                <Input
                  type="text"
                  id="clientName"
                  value={clientName}
                  onChange={handleClientNameChange}
                  required
                  placeholder=" "
                  hasIcon
                />
                <InputLabel htmlFor="clientName" hasValue={clientName.length > 0}>
                  Client Name
                </InputLabel>
              </InputContainer>
              
              <InputContainer>
                <InputIcon>
                  <FiPhone />
                </InputIcon>
                <Input
                  type="tel"
                  id="contactNumber"
                  value={contactNumber}
                  onChange={handleContactNumberChange}
                  required
                  placeholder=" "
                  hasIcon
                />
                <InputLabel htmlFor="contactNumber" hasValue={contactNumber.length > 0}>
                  Client Contact Number
                </InputLabel>
              </InputContainer>

              <InputContainer>
                <InputIcon>
                  <FiUser />
                </InputIcon>
                <Input
                  type="text"
                  id="architectName"
                  value={architectName}
                  onChange={handleArchitectNameChange}
                  required
                  placeholder=" "
                  hasIcon
                />
                <InputLabel htmlFor="architectName" hasValue={architectName.length > 0}>
                  Architect Name
                </InputLabel>
              </InputContainer>

              <InputContainer>
                <InputIcon>
                  <FiPhone />
                </InputIcon>
                <Input
                  type="tel"
                  id="architectNumber"
                  value={architectNumber}
                  onChange={handleArchitectNumberChange}
                  required
                  placeholder=" "
                  hasIcon
                />
                <InputLabel htmlFor="architectNumber" hasValue={architectNumber.length > 0}>
                  Architect Contact Number
                </InputLabel>
              </InputContainer>

              <InputContainer>
                <InputIcon>
                  <FiHardDrive />
                </InputIcon>
                <Input
                  type="text"
                  value={salesAdvisoryName}
                  disabled
                  isDisabled
                  readOnly
                  placeholder=" "
                  hasIcon
                />
                <InputLabel hasValue={true} isDisabled>
                  Sales Advisory Name
                </InputLabel>
              </InputContainer>

              <ScanButton
                type="button"
                onClick={handleScanClick}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Scan QR code
              </ScanButton>
            </Form>
          </Section>

          <Section>
            <SectionTitle>SHORTLISTED PRODUCTS</SectionTitle>
            <div style={{ padding: '15px' }}>
              {products.length > 0 && (
                <m.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <ProductList>
                    {/* <SubSection>
                      <SubSectionTitle>ROOMS</SubSectionTitle> */}
                      <SubSectionContent>
                        {Object.entries(
                          products.reduce((acc, product) => {
                            if (!acc[product.room]) {
                              acc[product.room] = [];
                            }
                            acc[product.room].push(product);
                            return acc;
                          }, {} as { [key: string]: Product[] })
                        ).map(([room, roomProducts]) => (
                          <ProductItem key={room}>
                            <ProductInfo>
                              <InfoColumn>
                                <h3>Item Code</h3>
                                <p>{roomProducts[0].code}</p>
                              </InfoColumn>
                              <InfoColumn>
                                <h3>Room Name</h3>
                                <p>{room}</p>
                              </InfoColumn>
                              <InfoColumn>
                                <h3>Application / Area</h3>
                                <p>{roomProducts[0].application}</p>
                              </InfoColumn>
                            </ProductInfo>
                            <ActionButtons>
                              <ActionButton type="button" onClick={handleViewProduct}>
                                <FiEye size={16} />
                              </ActionButton>
                              <ActionButton type="button" onClick={() => handleDeleteClick(room)}>
                                <FiTrash2 size={16} />
                              </ActionButton>
                            </ActionButtons>
                          </ProductItem>
                        ))}
                      </SubSectionContent>
                    {/* </SubSection> */}
                  </ProductList>
                </m.div>
              )}
            </div>
          </Section>
          <ButtonGroup>
            <Button
              onClick={() => navigate('/home')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Save
            </Button>
          </ButtonGroup>
        </Content>

        {showScanner && (
          <QRScanner
            onClose={() => setShowScanner(false)}
            onScan={handleScan}
          />
        )}

        {showProductView && (
          <SurfaceProductView
            productData={dummyProductDetails}
            onClose={() => setShowProductView(false)}
            onShortlist={() => setShowProductView(false)}
          />
        )}

        {showDeleteConfirm && (
          <DeleteConfirmModal
            onClose={() => setShowDeleteConfirm(false)}
            onConfirm={handleConfirmDelete}
            roomName={roomToDelete || ''}
          />
        )}

        {showLogoutConfirm && (
          <LogoutModal onClose={() => setShowLogoutConfirm(false)} />
        )}
      </PageContainer>
    </LazyMotion>
  );
};

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 24px;
  width: 100%;
  max-width: 320px;
  text-align: center;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  color: #333;
  margin-bottom: 12px;
  font-weight: 600;
`;

const ModalText = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 24px;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const ModalButton = styled(motion.button)<{ variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  background: ${props => props.variant === 'primary' ? '#ff0000' : '#f0f0f0'};
  color: ${props => props.variant === 'primary' ? 'white' : '#333'};
`;

interface DeleteConfirmModalProps {
  onClose: () => void;
  onConfirm: () => void;
  roomName: string;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({ onClose, onConfirm, roomName }) => {
  return (
    <ModalOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <ModalContent
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={e => e.stopPropagation()}
      >
        <ModalTitle>Delete Product</ModalTitle>
        <ModalText>Are you sure you want to delete "{roomName}" and all its products?</ModalText>
        <ModalButtons>
          <ModalButton
            onClick={onClose}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Cancel
          </ModalButton>
          <ModalButton
            variant="primary"
            onClick={onConfirm}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Delete
          </ModalButton>
        </ModalButtons>
      </ModalContent>
    </ModalOverlay>
  );
};

export default NewEnquiry; 